import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import { useRef } from 'react'
import useOnScreen from '../../../../ui/hooks/useOnScreen'

import withMemo from '../../../../../utils/withMemo'
import CategoryAdv from './categoryAdv'
import ProductCategoryGridItem from './categoryGridItem'
import ProductsHeader from './productsHeader'
import ProductsShowAllCta from './productsShowAllCta'

const StyledRoot = styled.div(() => ({
  width: '100%',
  display: 'inline-block',
}))

const StyledGrid = styled.div(({ theme: { spaces, breakpoints } }) => ({
  paddingBottom: spaces.default,
  display: 'grid',
  gridTemplateColumns: `repeat(2, calc(50% - ${spaces.default / 2}px))`,
  gridGap: `${spaces.default}px ${spaces.default}px`,
  [`@media (min-width: ${breakpoints.small})`]: {
    gridTemplateColumns: `repeat(4, calc(25% - ${spaces.default * 0.813}px))`,
  },
  [`@media (min-width: ${breakpoints.xlarge})`]: {
    gridTemplateColumns: `repeat(6, calc(16.666% - ${spaces.default * 0.844}px))`,
  },
}))

const StyledGridHeader = styled.div(({ theme: { colors } }) => ({
  padding: '15px 0px',
  borderTop: `1px solid ${colors.light_grey}`,
}))

const StyledGridShowAllCTAItem = styled.div(({ theme: { breakpoints }, productsExpanded }) => ({
  gridColumn: '1 / -1',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  [`@media (min-width: ${breakpoints.small})`]: {
    gridColumn: !productsExpanded ? '3 / 5' : '1 / -1',
  },
}))

const executeScroll = ref => ref.current.scrollIntoView({ behavior: 'smooth' })

const ProductsCategoryBlock = ({
  category, products, flyerId, clientType,
  hasOnlyOtherDealsCategory, brandColorBg, brandColorText, isCategoryExpanded,
  expandProductsLabel, collapseProductsLabel,
  onProductClick, onProductVisible, onFirstEnterScreen, onExpandProductsClick,
  country,
}) => {
  const categoryBlockRef = useRef(null)
  const categoryHeaderRef = useRef()
  const categoryAdvSlotRef = useRef()

  useOnScreen({
    target: categoryHeaderRef,
    onEnterScreen: () => {
      if (categoryHeaderRef.current) {
        if (categoryAdvSlotRef.current && onFirstEnterScreen) {
          const advData = categoryAdvSlotRef.current.getAdvData()
          onFirstEnterScreen(advData)
        }

        /**
         * The categoryHeaderRef.current value is updated in order to fire the
         * onEnterScreen callback only once
         */
        categoryHeaderRef.current = false
      }
    },
  }, {})

  const expandProductsList = () => {
    onExpandProductsClick(category, !isCategoryExpanded)
    if (isCategoryExpanded) executeScroll(categoryBlockRef)
  }

  const showExpandProductsCTA = products.length > 6
  const filteredProducts = isCategoryExpanded ? products : products.slice(0, 6)

  return (
    <StyledRoot ref={categoryBlockRef}>
      <StyledGridHeader ref={categoryHeaderRef}>
        { !hasOnlyOtherDealsCategory ? (
          <ProductsHeader
            category={category}
            brandColorBg={brandColorBg}
            brandColorText={brandColorText}
          />
        ) : null}
      </StyledGridHeader>
      <StyledGrid data-test="ProductsGrid">
        {filteredProducts.map((product, index) => (
          <ProductCategoryGridItem
            key={product.id}
            data-test="ProductsGridItem"
            category={category}
            product={product}
            position={index + 1}
            onProductClick={onProductClick}
            onProductVisible={onProductVisible}
          />
        ))}
        {showExpandProductsCTA && (
          <StyledGridShowAllCTAItem productsExpanded={isCategoryExpanded}>
            <ProductsShowAllCta
              expandProductsLabel={expandProductsLabel}
              collapseProductsLabel={collapseProductsLabel}
              isBlockExpanded={isCategoryExpanded}
              onExpandProductsClick={expandProductsList}
            />
          </StyledGridShowAllCTAItem>
        )}
      </StyledGrid>
      <CategoryAdv
        ref={categoryAdvSlotRef}
        category={category}
        flyerId={flyerId}
        clientType={clientType}
        country={country}
      />
    </StyledRoot>
  )
}

ProductsCategoryBlock.propTypes = {
  products: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape({}))),
    PropTypes.array,
  ]),
  category: PropTypes.string,
  flyerId: PropTypes.string,
  clientType: PropTypes.oneOf(['app', 'web']),
  hasOnlyOtherDealsCategory: PropTypes.bool,
  brandColorBg: PropTypes.string,
  brandColorText: PropTypes.string,
  isCategoryExpanded: PropTypes.bool,
  expandProductsLabel: PropTypes.string,
  collapseProductsLabel: PropTypes.string,
  country: PropTypes.string,
  onProductClick: PropTypes.func,
  onProductVisible: PropTypes.func,
  onFirstEnterScreen: PropTypes.func,
  onExpandProductsClick: PropTypes.func,
}

ProductsCategoryBlock.defaultProps = {
  products: [],
  category: '',
  flyerId: '',
  clientType: 'web',
  hasOnlyOtherDealsCategory: false,
  brandColorBg: '',
  brandColorText: '',
  isCategoryExpanded: false,
  expandProductsLabel: '',
  collapseProductsLabel: '',
  country: '',
  onProductClick: () => false,
  onProductVisible: () => false,
  onFirstEnterScreen: () => false,
  onExpandProductsClick: () => false,
}

export default withMemo(
  ProductsCategoryBlock,
  ['products', 'category', 'hasOnlyOtherDealsCategory', 'brandColorBg', 'brandColorText', 'isCategoryExpanded'],
)
